import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Tag } from 'primereact/tag';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTranslation } from '../../locales/useTranslation';
import {
    getPendingExtensions,
    getAllExtensionRequests,
    approveExtension,
    rejectExtension,
    getLicenseHistory
} from '../../services/api';
import eventBus from '../../eventBus';
import { useAuthGuard } from '../../hooks/useAuthGuard';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../shared/PageHeader';

interface ExtensionRequest {
    id: number;
    license_id: number;
    requested_by: string;
    requested_date: string;
    extension_date: string;
    status: 'PENDING' | 'APPROVED' | 'REJECTED';
    remarks?: string;
    approved_by?: string;
    approved_at?: string;
    toolName?: string;
    userId?: string;
    serverInfo?: string;
    currentEndDate?: string;
    rejectionReason?: string;
}

const ExtensionApprovalPage: React.FC = () => {
    const { isSidebarOpen } = useSidebar();
    const useNew = useNewGui();
    const t = useTranslation();
    const { isAuthenticated, authValidated } = useAuthGuard();

    // Data states
    const [pendingRequests, setPendingRequests] = useState<ExtensionRequest[]>([]);
    const [allRequests, setAllRequests] = useState<ExtensionRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequests, setSelectedRequests] = useState<ExtensionRequest[]>([]);

    // License History Modal states
    const [showLicenseHistory, setShowLicenseHistory] = useState(false);
    const [selectedLicenseHistory, setSelectedLicenseHistory] = useState<any[]>([]);
    const [selectedLicenseSummary, setSelectedLicenseSummary] = useState<any>(null);

    // Filter states
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [userFilter, setUserFilter] = useState<string>('');
    const [toolFilter, setToolFilter] = useState<string>('');
    const [dateRange, setDateRange] = useState<Date[] | null>(null);

    // Modal states
    const [showApprovalDialog, setShowApprovalDialog] = useState(false);
    const [showRejectionDialog, setShowRejectionDialog] = useState(false);
    const [showBulkDialog, setShowBulkDialog] = useState(false);
    const [currentAction, setCurrentAction] = useState<'approve' | 'reject' | null>(null);
    const [actionReason, setActionReason] = useState('');

    // Status options
    const statusOptions = [
        { label: 'All Statuses', value: 'ALL' },
        { label: 'Pending', value: 'PENDING' },
        { label: 'Approved', value: 'APPROVED' },
        { label: 'Rejected', value: 'REJECTED' }
    ];

    // Fetch pending extension requests
    const fetchPendingRequests = async () => {
        try {
            setLoading(true);
            const data = await getPendingExtensions();
            setPendingRequests(data || []);
        } catch (error) {
            console.error('Error fetching pending requests:', error);
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: 'Failed to load pending extension requests',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Fetch all requests (for history tab)
    const fetchAllRequests = async () => {
        try {
            const params: any = {};
            if (statusFilter !== 'ALL') params.status = statusFilter;
            if (userFilter) params.userId = userFilter;
            if (dateRange && dateRange.length === 2) {
                params.startDate = dateRange[0].toISOString();
                params.endDate = dateRange[1].toISOString();
            }

            const data = await getAllExtensionRequests(params);
            setAllRequests(data || []);
        } catch (error) {
            console.error('Error fetching all requests:', error);
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: 'Failed to load extension request history',
                severity: 'error'
            });
        }
    };

    // Handle individual approval
    const handleApprove = (request: ExtensionRequest) => {
        setSelectedRequests([request]);
        setCurrentAction('approve');
        setShowApprovalDialog(true);
    };

    // Handle individual rejection
    const handleReject = (request: ExtensionRequest) => {
        setSelectedRequests([request]);
        setCurrentAction('reject');
        setShowRejectionDialog(true);
    };

    // Handle bulk approval
    const handleBulkApprove = () => {
        if (selectedRequests.length === 0) {
            eventBus.emit('show-toast', {
                summary: 'No Selection',
                message: 'Please select requests to approve',
                severity: 'warn'
            });
            return;
        }
        setCurrentAction('approve');
        setShowBulkDialog(true);
    };

    // Handle bulk rejection
    const handleBulkReject = () => {
        if (selectedRequests.length === 0) {
            eventBus.emit('show-toast', {
                summary: 'No Selection',
                message: 'Please select requests to reject',
                severity: 'warn'
            });
            return;
        }
        setCurrentAction('reject');
        setShowBulkDialog(true);
    };

    // Submit approval/rejection
    const submitAction = async () => {
        try {
            setLoading(true);
            const isApproval = currentAction === 'approve';

            for (const request of selectedRequests) {
                if (isApproval) {
                    await approveExtension(request.id);
                } else {
                    await rejectExtension(request.id, actionReason);
                }
            }

            eventBus.emit('show-toast', {
                summary: 'Success',
                message: `${isApproval ? 'Approved' : 'Rejected'} ${selectedRequests.length} extension request${selectedRequests.length > 1 ? 's' : ''}`,
                severity: 'success'
            });

            // Close dialogs and refresh data
            setShowApprovalDialog(false);
            setShowRejectionDialog(false);
            setShowBulkDialog(false);
            setSelectedRequests([]);
            setActionReason('');
            await fetchPendingRequests();

        } catch (error) {
            console.error('Error processing requests:', error);
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: `Failed to ${currentAction} extension requests`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Clear filters
    const clearFilters = () => {
        setStatusFilter('PENDING');
        setUserFilter('');
        setToolFilter('');
        setDateRange(null);
    };

    // Get filtered data
    const getFilteredData = (data: ExtensionRequest[]) => {
        return data.filter(request => {
            const statusMatch = statusFilter === 'ALL' || request.status === statusFilter;

            // Enhanced user filtering - more permissive search
            const userMatch = !userFilter ||
                (request.userId?.toLowerCase().includes(userFilter.toLowerCase()) ||
                 request.requested_by?.toLowerCase().includes(userFilter.toLowerCase()));

            const toolMatch = !toolFilter || request.toolName?.toLowerCase().includes(toolFilter.toLowerCase());

            let dateMatch = true;
            if (dateRange && dateRange.length === 2) {
                const requestDate = new Date(request.requested_date);
                dateMatch = requestDate >= dateRange[0] && requestDate <= dateRange[1];
            }

            return statusMatch && userMatch && toolMatch && dateMatch;
        });
    };

    // Status badge renderer
    const renderStatus = (rowData: ExtensionRequest) => {
        const statusConfig = {
            PENDING: { severity: 'warn', label: 'Pending' },
            APPROVED: { severity: 'success', label: 'Approved' },
            REJECTED: { severity: 'danger', label: 'Rejected' }
        };

        const config = statusConfig[rowData.status] || { severity: 'info', label: rowData.status };

        return (
            <Tag severity={config.severity as any} value={config.label} />
        );
    };

    // Date renderer
    const renderDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    // Action buttons renderer
    const renderActions = (rowData: ExtensionRequest) => {
        if (rowData.status !== 'PENDING') return null;

        return (
            <div className="flex gap-2">
                <Button
                    label="Approve"
                    icon="pi pi-check"
                    size="small"
                    className={approveBtnClass(false)}
                    onClick={() => handleApprove(rowData)}
                />
                <Button
                    label="Reject"
                    icon="pi pi-times"
                    size="small"
                    className={rejectBtnClass(false)}
                    onClick={() => handleReject(rowData)}
                />
            </div>
        );
    };

    // Load data on mount - only when authenticated
    useEffect(() => {
        if (isAuthenticated && authValidated) {
            fetchPendingRequests();
            fetchAllRequests(); // Fix: Also load history data on mount
        }
    }, [isAuthenticated, authValidated]);

    // License History Modal functions
    const openLicenseHistoryModal = async (licenseId: number) => {
        try {
            setLoading(true);
            // Fetch license history from API
            const data = await getLicenseHistory(licenseId);
            setSelectedLicenseSummary(data.summary);
            setSelectedLicenseHistory(data.history);
            setShowLicenseHistory(true);
        } catch (error) {
            console.error('Error fetching license history:', error);
            eventBus.emit('show-toast', {
                summary: 'Error',
                message: 'Failed to load license history',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Update all requests when filters change
    useEffect(() => {
        fetchAllRequests();
    }, [statusFilter, userFilter, toolFilter, dateRange]);

    const pendingCount = pendingRequests.length;
    const filteredPending = getFilteredData(pendingRequests);
    const filteredAll = getFilteredData(allRequests);

    // Dynamic status color based on pending count.
    // New GUI: keep solid token borders (not /30) so the chip stays as bold as classic green-500.
    const getStatusColor = (count: number) => {
        if (useNew) {
            if (count === 0) return { bg: 'bg-success/15', border: 'border-success border-2', text: 'text-success', severity: 'success' as const };
            if (count < 5) return { bg: 'bg-info/15', border: 'border-info border-2', text: 'text-info', severity: 'info' as const };
            if (count <= 15) return { bg: 'bg-warning/15', border: 'border-warning border-2', text: 'text-warning', severity: 'warning' as const };
            return { bg: 'bg-danger/15', border: 'border-danger border-2', text: 'text-danger', severity: 'danger' as const };
        }
        if (count === 0) return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', severity: 'success' as const };
        if (count < 5) return { bg: 'bg-emerald-50', border: 'border-emerald-500', text: 'text-emerald-700', severity: 'info' as const };
        if (count <= 15) return { bg: 'bg-yellow-50', border: 'border-yellow-500', text: 'text-yellow-700', severity: 'warning' as const };
        return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', severity: 'danger' as const };
    };

    const statusStyle = getStatusColor(pendingCount);

    const approveBtnClass = useNew
        ? (disabled: boolean) => disabled
            ? 'opacity-50 cursor-not-allowed bg-surface-raised text-ink-secondary border-border-subtle px-3 py-1'
            : 'bg-success hover:opacity-90 text-white border-success px-3 py-1'
        : () => 'bg-green-600 hover:bg-green-700 text-white border-green-600 px-3 py-1';

    const rejectBtnClass = useNew
        ? (disabled: boolean) => disabled
            ? 'opacity-50 cursor-not-allowed bg-surface-raised text-ink-secondary border-border-subtle px-3 py-1'
            : 'bg-danger hover:opacity-90 text-white border-danger px-3 py-1'
        : () => 'bg-red-600 hover:bg-red-700 text-white border-red-600 px-3 py-1';

    const filterLabelClass = `block text-sm font-medium mb-1 ${useNew ? 'text-ink-secondary' : 'text-gray-700'}`;

    const shellClass = `flex-1 p-4 transition-all duration-300 ${
        useNew ? 'bg-surface-app font-plex' : 'bg-white'
    } ${isSidebarOpen ? 'ml-64' : 'ml-20'}`;

    return (
        <div className={shellClass}>
            {/* Breadcrumb Navigation */}
            <div className="mb-4">
                <nav className={`flex text-sm ${useNew ? 'text-ink-secondary' : 'text-gray-600'}`}>
                    <span className={useNew ? 'hover:text-accent cursor-pointer' : 'hover:text-blue-600 cursor-pointer'}>{t.breadcrumbSystemDashboard}</span>
                    <span className="mx-2">→</span>
                    <span className={useNew ? 'hover:text-accent cursor-pointer' : 'hover:text-blue-600 cursor-pointer'}>{t.breadcrumbLicenses}</span>
                    <span className="mx-2">→</span>
                    <span className={useNew ? 'text-ink-primary font-medium' : 'text-gray-800 font-medium'}>{t.breadcrumbLicWorkflow}</span>
                </nav>
            </div>

            {/* Compact Header */}
            {useNew ? (
                <div className="mb-3">
                    <PageHeader
                        title="Extension Approvals"
                        purpose={t.manageExtensionRequests}
                        actions={
                            <>
                                <div className={`flex items-center gap-2 ${statusStyle.bg} px-3 py-1 rounded-lg border ${statusStyle.border}`}>
                                    <Badge value={pendingCount} severity={statusStyle.severity} />
                                    <span className={`text-sm font-medium ${statusStyle.text}`}>{t.pendingRequests}</span>
                                </div>
                                <Button
                                    label="Refresh"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    size="small"
                                    onClick={async () => {
                                        await fetchPendingRequests();
                                        await fetchAllRequests();
                                    }}
                                    loading={loading}
                                />
                            </>
                        }
                    />
                </div>
            ) : (
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">
                        {t.extensionApprovals}
                    </h1>
                    <p className="text-sm text-gray-600 mt-1">
                        {t.manageExtensionRequests}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className={`flex items-center gap-2 ${statusStyle.bg} px-3 py-1 rounded-lg border ${statusStyle.border}`}>
                        <Badge value={pendingCount} severity={statusStyle.severity} />
                        <span className={`text-sm font-medium ${statusStyle.text}`}>{t.pendingRequests}</span>
                    </div>
                    <Button
                        label="Refresh"
                        icon="pi pi-refresh"
                        severity="secondary"
                        size="small"
                        onClick={async () => {
                            await fetchPendingRequests();
                            await fetchAllRequests();
                        }}
                        loading={loading}
                    />
                </div>
            </div>
            )}

            {/* Compact Bulk Actions */}
            <div className={useNew
                ? 'flex gap-2 mb-3 py-2 px-3 bg-surface-raised rounded-md border-2 border-border-strong'
                : 'flex gap-2 mb-3 py-2 px-3 bg-slate-50 rounded-md border'
            }>
                <Button
                    label={`${t.approveSelected} (${selectedRequests.length})`}
                    icon="pi pi-check"
                    size="small"
                    className={approveBtnClass(selectedRequests.length === 0)}
                    disabled={selectedRequests.length === 0}
                    onClick={handleBulkApprove}
                />
                <Button
                    label={`${t.rejectSelected} (${selectedRequests.length})`}
                    icon="pi pi-times"
                    size="small"
                    className={rejectBtnClass(selectedRequests.length === 0)}
                    disabled={selectedRequests.length === 0}
                    onClick={handleBulkReject}
                />
                <div className={`text-xs self-center ml-2 ${useNew ? 'text-ink-secondary' : 'text-gray-500'}`}>
                    {selectedRequests.length > 0 ? `${selectedRequests.length} selected` : t.selectRequestsToBulkProcess}
                </div>
            </div>

            {/* Collapsible Filters */}
            <Accordion className="mb-4">
                <AccordionTab
                    header="Filters"
                    headerClassName={useNew ? 'text-sm font-medium py-2 text-ink-secondary' : 'text-sm font-medium py-2'}
                >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
                        <div>
                            <label className={filterLabelClass}>Status</label>
                            <Dropdown
                                value={statusFilter}
                                options={statusOptions}
                                onChange={(e) => setStatusFilter(e.value)}
                                placeholder="Select status"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className={filterLabelClass}>User ID</label>
                            <InputText
                                value={userFilter}
                                onChange={(e) => setUserFilter(e.target.value)}
                                placeholder="Partial match (e.g., 'john', 'user')"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className={filterLabelClass}>Tool Name</label>
                            <InputText
                                value={toolFilter}
                                onChange={(e) => setToolFilter(e.target.value)}
                                placeholder="Partial match (e.g., 'cad', 'design')"
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className={filterLabelClass}>Request Date Range</label>
                            <Calendar
                                value={dateRange}
                                onChange={(e) => setDateRange(e.value as Date[] | null)}
                                selectionMode="range"
                                placeholder="Select date range"
                                className="w-full"
                                showButtonBar
                            />
                        </div>
                    </div>

                    <div className="flex gap-2 mt-4 pt-2 border-t">
                        <Button
                            label="Clear All Filters"
                            icon="pi pi-filter-slash"
                            severity="secondary"
                            size="small"
                            onClick={clearFilters}
                        />
                        <div className="text-sm text-gray-600 self-center">
                            Showing {statusFilter === 'PENDING' ? filteredPending.length : filteredAll.length} of {statusFilter === 'PENDING' ? pendingRequests.length : allRequests.length} requests
                        </div>
                    </div>
                </AccordionTab>
            </Accordion>

            {/* Tabbed Interface — keep colored tab/table framing (tokenized) so new GUI does not wash out classic blue/green weight */}
            <TabView className={useNew ? 'border-2 border-border-strong rounded-lg overflow-hidden' : 'border border-gray-200 rounded-lg overflow-hidden'}>
                <TabPanel
                    header={`Pending Requests (${filteredPending.length})`}
                    headerClassName={useNew
                        ? 'font-medium px-4 py-3 bg-accent/10 border-b-2 border-accent text-accent'
                        : 'font-medium px-4 py-3 bg-blue-50 border-b-2 border-blue-500 text-blue-700'}
                >
                    <DataTable
                        value={filteredPending}
                        paginator
                        rows={10}
                        selectionMode="multiple"
                        selection={selectedRequests}
                        onSelectionChange={(e) => setSelectedRequests(e.value)}
                        dataKey="id"
                        className={useNew ? 'p-datatable-striped new-gui-datatable border-2 border-accent/40' : 'p-datatable-striped border border-blue-200'}
                        loading={loading}
                        emptyMessage="No pending extension requests found"
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
                        <Column field="id" header="Request ID" sortable style={{ width: '100px' }} />
                        <Column field="license_id" header="License ID" sortable style={{ width: '100px' }} body={(rowData) => (
                            <button
                                className={useNew ? 'text-accent hover:text-accent-hover underline' : 'text-blue-600 hover:text-blue-800 underline'}
                                onClick={() => openLicenseHistoryModal(rowData.license_id)}
                            >
                                {rowData.license_id}
                            </button>
                        )} />
                        <Column field="userId" header="User ID" sortable filter body={(rowData) => (
                            <button
                                className={useNew ? 'text-accent hover:text-accent-hover underline' : 'text-blue-600 hover:text-blue-800 underline'}
                                onClick={() => {
                                    // Navigate to user details page
                                    window.location.href = `/user/${rowData.userId}`;
                                }}
                            >
                                {rowData.userId}
                            </button>
                        )} />
                        <Column field="toolName" header="Tool" sortable filter />
                        <Column
                            field="requested_date"
                            header="Request Date"
                            sortable
                            body={(rowData) => renderDate(rowData.requested_date)}
                            style={{ width: '120px' }}
                        />
                        <Column
                            field="extension_date"
                            header="New End Date"
                            sortable
                            body={(rowData) => renderDate(rowData.extension_date)}
                            style={{ width: '120px' }}
                        />
                        <Column field="remarks" header="Remarks" style={{ minWidth: '200px' }} />
                        <Column
                            field="status"
                            header="Status"
                            body={renderStatus}
                            style={{ width: '100px' }}
                        />
                        <Column
                            header="Actions"
                            body={renderActions}
                            style={{ minWidth: '150px' }}
                        />
                    </DataTable>
                </TabPanel>

                <TabPanel
                    header={`Approval History (${filteredAll.length})`}
                    headerClassName={useNew
                        ? 'font-medium px-4 py-3 bg-success/10 border-b-2 border-success text-success'
                        : 'font-medium px-4 py-3 bg-green-50 border-b-2 border-green-500 text-green-700'}
                >
                    <DataTable
                        value={filteredAll}
                        paginator
                        rows={10}
                        dataKey="id"
                        className={useNew ? 'p-datatable-striped new-gui-datatable border-2 border-success/40' : 'p-datatable-striped border border-green-200'}
                        loading={loading}
                        emptyMessage="No processed requests found"
                    >
                        <Column field="id" header="Request ID" sortable style={{ width: '100px' }} />
                        <Column field="license_id" header="License ID" sortable style={{ width: '100px' }} body={(rowData) => (
                            <button
                                className={useNew ? 'text-accent hover:text-accent-hover underline' : 'text-blue-600 hover:text-blue-800 underline'}
                                onClick={() => openLicenseHistoryModal(rowData.license_id)}
                            >
                                {rowData.license_id}
                            </button>
                        )} />
                        <Column field="userId" header="User ID" sortable filter />
                        <Column field="toolName" header="Tool" sortable filter />
                        <Column
                            field="requested_date"
                            header="Request Date"
                            sortable
                            body={(rowData) => renderDate(rowData.requested_date)}
                            style={{ width: '120px' }}
                        />
                        <Column
                            field="extension_date"
                            header="New End Date"
                            sortable
                            body={(rowData) => renderDate(rowData.extension_date)}
                            style={{ width: '120px' }}
                        />
                        <Column
                            field="approved_at"
                            header="Decision Date"
                            sortable
                            body={(rowData) => rowData.approved_at ? renderDate(rowData.approved_at) : 'N/A'}
                            style={{ width: '120px' }}
                        />
                        <Column field="approved_by" header="Approved By" sortable />
                        <Column field="remarks" header="Remarks" style={{ minWidth: '200px' }} />
                        <Column
                            field="status"
                            header="Status"
                            body={renderStatus}
                            style={{ width: '100px' }}
                        />
                    </DataTable>
                </TabPanel>
            </TabView>

            {/* Approval Dialog */}
            <Dialog
                header="Confirm Approval"
                visible={showApprovalDialog}
                onHide={() => setShowApprovalDialog(false)}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            onClick={() => setShowApprovalDialog(false)}
                        />
                        <Button
                            label="Approve"
                            icon="pi pi-check"
                            severity="success"
                            onClick={submitAction}
                            loading={loading}
                        />
                    </div>
                }
                style={{ width: '400px' }}
            >
                <div className="p-4">
                    <p className="mb-4">
                        Are you sure you want to approve the selected extension request(s)?
                    </p>
                    <div className="bg-blue-50 p-3 rounded">
                        <p className="text-sm text-blue-800">
                            <strong>Requests to approve:</strong> {selectedRequests.length}
                        </p>
                    </div>
                </div>
            </Dialog>

            {/* Rejection Dialog */}
            <Dialog
                header="Confirm Rejection"
                visible={showRejectionDialog}
                onHide={() => setShowRejectionDialog(false)}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            onClick={() => setShowRejectionDialog(false)}
                        />
                        <Button
                            label="Reject"
                            icon="pi pi-times"
                            severity="danger"
                            onClick={submitAction}
                            loading={loading}
                        />
                    </div>
                }
                style={{ width: '500px' }}
            >
                <div className="p-4">
                    <p className="mb-4">
                        Please provide a reason for rejecting the extension request(s).
                    </p>

                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Rejection Reason *</label>
                        <textarea
                            value={actionReason}
                            onChange={(e) => setActionReason(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            rows={3}
                            placeholder="Please explain why this request is being rejected..."
                            required
                        />
                    </div>

                    <div className="bg-red-50 p-3 rounded">
                        <p className="text-sm text-red-800">
                            <strong>Requests to reject:</strong> {selectedRequests.length}
                        </p>
                    </div>
                </div>
            </Dialog>

            {/* Bulk Action Dialog */}
            <Dialog
                header={`Bulk ${currentAction === 'approve' ? 'Approval' : 'Rejection'}`}
                visible={showBulkDialog}
                onHide={() => setShowBulkDialog(false)}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            severity="secondary"
                            onClick={() => setShowBulkDialog(false)}
                        />
                        <Button
                            label={currentAction === 'approve' ? 'Approve All' : 'Reject All'}
                            icon={`pi pi-${currentAction === 'approve' ? 'check' : 'times'}`}
                            severity={currentAction === 'approve' ? 'success' : 'danger'}
                            onClick={submitAction}
                            loading={loading}
                        />
                    </div>
                }
                style={{ width: '700px' }}
            >
                <div className="p-4">
                    {currentAction === 'approve' ? (
                        <div>
                            <p className="mb-4">
                                Are you sure you want to approve all {selectedRequests.length} selected extension requests?
                            </p>

                            <div className="bg-green-50 p-3 rounded mb-4">
                                <p className="text-sm text-green-800">
                                    <strong>Requests to approve:</strong> {selectedRequests.length}
                                </p>
                            </div>

                            {/* Show selected requests details */}
                            <div className="border rounded-lg overflow-hidden">
                                <div className="bg-gray-100 px-3 py-2 border-b">
                                    <h4 className="text-sm font-medium text-gray-700">Selected Requests Details</h4>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    {selectedRequests.map((request, index) => (
                                        <div key={request.id} className={`px-3 py-2 border-b last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <div className="grid grid-cols-4 gap-2 text-sm">
                                                <div>
                                                    <span className="font-medium text-gray-600">ID:</span>
                                                    <span className="ml-1">{request.id}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">User:</span>
                                                    <span className="ml-1">{request.userId}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">Tool:</span>
                                                    <span className="ml-1">{request.toolName}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">New Date:</span>
                                                    <span className="ml-1">{renderDate(request.extension_date)}</span>
                                                </div>
                                            </div>
                                            {request.remarks && (
                                                <div className="mt-1 text-xs text-gray-600">
                                                    <span className="font-medium">Remarks:</span> {request.remarks}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="mb-4">
                                Please provide a reason for rejecting all {selectedRequests.length} selected extension requests.
                            </p>

                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Bulk Rejection Reason *</label>
                                <textarea
                                    value={actionReason}
                                    onChange={(e) => setActionReason(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    rows={3}
                                    placeholder="Please explain why these requests are being rejected..."
                                    required
                                />
                            </div>

                            <div className="bg-red-50 p-3 rounded mb-4">
                                <p className="text-sm text-red-800">
                                    <strong>Requests to reject:</strong> {selectedRequests.length}
                                </p>
                            </div>

                            {/* Show selected requests details */}
                            <div className="border rounded-lg overflow-hidden">
                                <div className="bg-gray-100 px-3 py-2 border-b">
                                    <h4 className="text-sm font-medium text-gray-700">Selected Requests Details</h4>
                                </div>
                                <div className="max-h-60 overflow-y-auto">
                                    {selectedRequests.map((request, index) => (
                                        <div key={request.id} className={`px-3 py-2 border-b last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                            <div className="grid grid-cols-4 gap-2 text-sm">
                                                <div>
                                                    <span className="font-medium text-gray-600">ID:</span>
                                                    <span className="ml-1">{request.id}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">User:</span>
                                                    <span className="ml-1">{request.userId}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">Tool:</span>
                                                    <span className="ml-1">{request.toolName}</span>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">New Date:</span>
                                                    <span className="ml-1">{renderDate(request.extension_date)}</span>
                                                </div>
                                            </div>
                                            {request.remarks && (
                                                <div className="mt-1 text-xs text-gray-600">
                                                    <span className="font-medium">Remarks:</span> {request.remarks}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>

            {/* License History Modal */}
            <Dialog
                header="License History"
                visible={showLicenseHistory}
                onHide={() => setShowLicenseHistory(false)}
                style={{ width: '900px', maxWidth: '90vw' }}
                maximizable
                className="p-fluid"
            >
                {selectedLicenseSummary && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-4">License Summary</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                            <div><strong>License ID:</strong> {selectedLicenseSummary.licenseId}</div>
                            <div><strong>User:</strong> {selectedLicenseSummary.infraUser}</div>
                            <div><strong>Tool:</strong> {selectedLicenseSummary.toolName}</div>
                            <div><strong>License Count:</strong> {selectedLicenseSummary.numLicenses}</div>
                            <div><strong>License Server:</strong> {selectedLicenseSummary.licSrvrConnectStr}</div>
                            <div><strong>Start Date:</strong> {new Date(selectedLicenseSummary.initialStartDate).toLocaleDateString()}</div>
                            <div><strong>Current End Date:</strong> {new Date(selectedLicenseSummary.currentPlannedEndDate).toLocaleDateString()}</div>
                            <div><strong>Extensions:</strong> {selectedLicenseSummary.numExtensions}</div>
                            <div><strong>Total Extension Days:</strong> {selectedLicenseSummary.totalExtensionDays}</div>
                            <div><strong>Last Updated:</strong> {new Date(selectedLicenseSummary.lastUpdated).toLocaleString()}</div>
                        </div>
                    </div>
                )}

                <div>
                    <h3 className="text-lg font-semibold mb-4">State History Timeline</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                        {selectedLicenseHistory.map((entry: any, index: number) => (
                            <div key={entry.id} className="border rounded-lg p-4 bg-white">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium">#{entry.id}</span>
                                        <span className="text-sm text-gray-500">
                                            {new Date(entry.changed_at).toLocaleString()}
                                        </span>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        entry.old_state === entry.new_state
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-green-100 text-green-800'
                                    }`}>
                                        {entry.old_state || 'Created'} → {entry.new_state}
                                    </span>
                                </div>

                                {entry.change_reason && (
                                    <div className="text-sm text-gray-700 mb-2">
                                        <strong>Details:</strong> {entry.change_reason}
                                    </div>
                                )}

                                <div className="text-xs text-gray-500">
                                    <strong>Operator:</strong> {entry.operator || 'System'}
                                    {entry.start_date && (
                                        <> | <strong>Start:</strong> {new Date(entry.start_date).toLocaleDateString()}</>
                                    )}
                                    {entry.planned_end_date && (
                                        <> | <strong>Planned End:</strong> {new Date(entry.planned_end_date).toLocaleDateString()}</>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default ExtensionApprovalPage;
